/**
 * Lenis smooth scroll — サイト全体に適用。
 *
 * - GSAP が読み込まれている場合は gsap.ticker に raf を乗せて駆動（アニメと同一クロック）。
 * - ScrollTrigger が存在する場合は scroll イベントで ScrollTrigger.update() を呼び、
 *   pin / スクロール連動アニメと同期させる（これが無いとズレる）。
 * - prefers-reduced-motion では初期化しない（アクセシビリティ）。
 * - タッチはデフォルトのネイティブスクロール（syncTouch:false）に任せ、モバイルの慣性を尊重。
 *
 * 読み込み順（theme.liquid）: gsap.min.js → ScrollTrigger.min.js → lenis.min.js → gsap-init.js → lenis-init.js
 */
(function () {
  function init() {
    if (typeof window.Lenis === 'undefined') {
      console.warn('[lenis-init] Lenis is not available. Check that lenis.min.js is included in theme.liquid.');
      return;
    }

    // モーション低減を希望するユーザーではスムーススクロールを無効化
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    var lenis = new Lenis({
      duration: 1.1,
      easing: function (t) {
        return Math.min(1, 1.001 - Math.pow(2, -10 * t));
      },
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.5,
    });

    // デバッグ・他スクリプトからの参照用に公開
    window.lenis = lenis;

    var hasGsap = typeof window.gsap !== 'undefined' && window.gsap.ticker;
    var hasScrollTrigger = typeof window.ScrollTrigger !== 'undefined';

    if (hasScrollTrigger) {
      lenis.on('scroll', window.ScrollTrigger.update);

      var ST = window.ScrollTrigger;

      // iOS 等のアドレスバー伸縮で発火する resize を GSAP 側で無視（公式の一次対策）。
      // これが無いと、スクロール中にアドレスバーが動くたび ST が自動 refresh され、
      // 全トリガーの同期再計測（強制レイアウト）でスクロールが一瞬カクつく／止まる。
      try { ST.config({ ignoreMobileResize: true }); } catch (e) {}

      // load / フォント読込 / 遅延ロード / リサイズ でレイアウトが変わると各 ScrollTrigger の
      // start/end 位置がズレるため再計算(refresh)が要る。ただし refresh は全トリガーの
      // 同期再計測（重い）なので、複数の発火経路を1本にまとめ、かつ「スクロールが少し落ち着いて
      // から（アイドル時）」実行して、スクロール中のヒッチを避ける。
      var refreshTimer;
      var scrollIdleTimer;
      var scrolling = false;
      lenis.on('scroll', function () {
        scrolling = true;
        clearTimeout(scrollIdleTimer);
        scrollIdleTimer = setTimeout(function () { scrolling = false; }, 150);
      });
      function scheduleRefresh() {
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(function run() {
          // スクロール中なら少し待って、静止してから refresh する
          if (scrolling) {
            refreshTimer = setTimeout(run, 160);
            return;
          }
          ST.refresh();
        }, 200);
      }

      window.addEventListener('load', scheduleRefresh);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(scheduleRefresh);
      }
      // 幅が変わったとき（回転・実リサイズ）だけ refresh。高さのみの変化（アドレスバー伸縮）は
      // ignoreMobileResize と合わせて二重に無視する。
      var lastVW = window.innerWidth;
      window.addEventListener('resize', function () {
        if (window.innerWidth === lastVW) return;
        lastVW = window.innerWidth;
        scheduleRefresh();
      });
    }

    if (hasGsap) {
      // GSAP のクロックで Lenis を駆動（アニメと同期）
      window.gsap.ticker.add(function (time) {
        lenis.raf(time * 1000); // gsap.ticker は秒、Lenis は ms
      });
      window.gsap.ticker.lagSmoothing(0);
    } else {
      // GSAP が無いページでは独自 raf ループ
      var raf = function (time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      };
      requestAnimationFrame(raf);
    }

    // 固定ヘッダーの高さ（--header-height。無ければヘッダー実測でフォールバック）
    function headerOffset() {
      var v = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
      var hh = parseFloat(v);
      if (!hh) {
        var el = document.querySelector('.section-header') || document.querySelector('.header');
        hh = el ? el.getBoundingClientRect().height : 0;
      }
      return hh || 0;
    }

    // ページ内固定ナビ（sticky）の高さ。実際に sticky 表示のときだけ加算
    function stickyNavOffset() {
      var nav = document.querySelector('[data-inpage-sticky-nav]');
      if (!nav) return 0;
      var section = nav.closest('.shopify-section') || nav;
      if (getComputedStyle(section).position !== 'sticky') return 0;
      return section.getBoundingClientRect().height || 0;
    }

    // アンカー遷移時の総オフセット（ヘッダー + 固定ページ内ナビ）
    function scrollOffset() {
      return headerOffset() + stickyNavOffset();
    }

    // ページ内アンカー（#id）を Lenis でスムーススクロール（固定ヘッダー分だけ上にオフセット）
    document.addEventListener('click', function (e) {
      var link = e.target && e.target.closest ? e.target.closest('a[href^="#"]') : null;
      if (!link) return;
      var hash = link.getAttribute('href');
      if (!hash || hash === '#') return;
      var target = null;
      try { target = document.querySelector(hash); } catch (_) { return; }
      if (!target) return;
      e.preventDefault();
      lenis.scrollTo(target, { offset: -scrollOffset() });
    });

    // 別ページから /#anchor で来た場合も、ヘッダー分オフセットして位置を補正
    if (window.location.hash && window.location.hash.length > 1) {
      var initialTarget = null;
      try { initialTarget = document.querySelector(window.location.hash); } catch (_) {}
      if (initialTarget) {
        setTimeout(function () {
          lenis.scrollTo(initialTarget, { offset: -scrollOffset(), immediate: true });
        }, 250);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ページ内固定ナビ(sticky)の高さを CSS 変数 --inpage-sticky-nav-height に反映。
  // sticky 列(.custom-flex-left 等)のオフセット計算に使用。Lenis 有無・reduced-motion に関わらず動く。
  function updateInpageNavVar() {
    var h = 0;
    var nav = document.querySelector('[data-inpage-sticky-nav]');
    if (nav) {
      var section = nav.closest('.shopify-section') || nav;
      if (getComputedStyle(section).position === 'sticky') {
        h = section.getBoundingClientRect().height || 0;
      }
    }
    document.documentElement.style.setProperty('--inpage-sticky-nav-height', h + 'px');
  }
  window.addEventListener('resize', updateInpageNavVar);
  window.addEventListener('load', updateInpageNavVar);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateInpageNavVar);
  } else {
    updateInpageNavVar();
  }
})();
